import React, {useEffect, useState} from 'react'
import {Field, FieldsManager} from '../../../data/services/fields'
import LocalStorage from '../../../util/localStorage'
import {useDispatch, useSelector} from 'react-redux'
import Resources from '../../../data/services/resources'
import {getSecondResource} from '../../../data/actions/secondResource'
import PayDateFilter from '../../accounting/carrier-pay/carrier-pay-date-filter'
import {
    classNames,
    getDefaultTableOptions,
    getProp,
    saveTableColumns
} from '../../../common/util/util-helpers'
import {download} from '../../../data/actions/download'
import {currentDate} from '../../../common/util/util-dates'
import DocumentArrowDownIcon from '@heroicons/react/20/solid/DocumentArrowDownIcon'
import Tippy from '@tippyjs/react'
import {CurrencyDollarIcon} from "@heroicons/react/24/outline"
import TableCard from "../../../common/components/resource-table/table-components/table-card"
import NoRecordsTable from "../../../common/components/no-records-found/no-records-table"
import TableFilters from "../../../common/components/resource-table/table-components/table-filters"
import ActiveFilters from "../../../common/components/resource-table/table-components/active-filters"
import Badge from "../../../common/components/badge"
import ResourceTable from "../../../common/components/resource-table"
import {excludeFields} from "../../../common/util/util-fields"
import moment from "moment"
import {LoaderSmall} from "../../../common/components/loader"
import {numberWithCommas} from "../../../util/util-formaters"
import Pagination from "../../../common/components/resource-table/table-components/pagination";
import TableCardFooter from "../../../common/components/resource-table/table-components/table-card-footer";

export default function ProfitMarginReportTab({
                                                  translate,
                                                  pagePath
                                              }) {

    /** Store
     ================================================================= */
    const dispatch = useDispatch()
    const secondResource = useSelector((state) => state.secondResource)
    const isLoading = secondResource.isLoading
    const data = secondResource?.data?.list ?? []
    const count = getProp(secondResource.data, 'count', 0)
    const totals = secondResource?.data?.totals ?? {}

    // Server-side totals (across all pages)
    const totalRevenue = totals.TotalRevenue ?? 0
    const totalCOGS = totals.TotalCOGS ?? 0
    const totalGrossProfit = totals.TotalGrossProfit ?? 0
    const totalSoldQty = totals.TotalSoldQty ?? 0
    const totalPurchaseCost = totals.TotalPurchaseCost ?? 0
    const overallMargin = totals.OverallProfitMarginPercent ?? 0

    /** Helpers
     ================================================================= */

    const getFields = () => {
        return {
            SKU: new Field('SKU', '', [''], false, 'text', {}),
            ProductService: new Field('ProductService', '', [''], false, 'text', {}),
            PurchasedQty: new Field('PurchasedQty', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.PurchasedQty ?? 0, 2)
            }),
            SoldQty: new Field('SoldQty', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.SoldQty ?? 0, 2)
            }),
            PeriodAvgCost: new Field('PeriodAvgCost', '', [''], false, 'decimal', {
                render: (item) => item.PeriodAvgCost ? numberWithCommas(item.PeriodAvgCost, 2) : '-'
            }),
            WeightedAvgCost: new Field('WeightedAvgCost', '', [''], false, 'decimal', {
                render: (item) => item.WeightedAvgCost ? numberWithCommas(item.WeightedAvgCost, 2) : '-'
            }),
            AvgSellPrice: new Field('AvgSellPrice', '', [''], false, 'decimal', {
                render: (item) => item.AvgSellPrice ? numberWithCommas(item.AvgSellPrice, 2) : '-'
            }),
            TotalPurchaseCost: new Field('TotalPurchaseCost', '', [''], false, 'decimal', {
                render: (item) => item.TotalPurchaseCost ? numberWithCommas(item.TotalPurchaseCost, 2) : '-'
            }),
            TotalRevenue: new Field('TotalRevenue', '', [''], false, 'decimal', {
                render: (item) => item.TotalRevenue ? numberWithCommas(item.TotalRevenue, 2) : '$0.00'
            }),
            COGS: new Field('COGS', '', [''], false, 'decimal', {
                render: (item) => item.COGS ? numberWithCommas(item.COGS, 2) : '$0.00'
            }),
            GrossProfit: new Field('GrossProfit', '', [''], false, 'decimal', {
                render: (item) => {
                    const profit = item.GrossProfit ?? 0
                    return (
                        <span>
                            ${numberWithCommas(profit, 2)}
                        </span>
                    )
                }
            }),
            ProfitMarginPercent: new Field('ProfitMarginPercent', '', [''], false, 'decimal', {
                render: (item) => {
                    const margin = item.ProfitMarginPercent ?? 0
                    const colorClass = margin >= 30 ? 'text-green-600' : margin >= 15 ? 'text-yellow-600' : 'text-red-600'
                    return (
                        <span className={colorClass + ' font-semibold'}>
                            {numberWithCommas(margin, 1)}%
                        </span>
                    )
                }
            }),
            MarkupPercent: new Field('MarkupPercent', '', [''], false, 'decimal', {
                render: (item) => item.MarkupPercent ? numberWithCommas(item.MarkupPercent, 1) + '%' : '-'
            }),
        }
    }

    const getQueryFields = () => {
        return {
            query: new Field('query', '', [''], false, 'search', {hideLabel: true, labelType: "float"}),
            StartDate: new Field('StartDate', moment().startOf('year').format('YYYY-MM-DD'), [], false, 'date', {}, {}),
            EndDate: new Field('EndDate', moment().format('YYYY-MM-DD'), [], false, 'date', {}, {}),
            offset: new Field('offset', '0', [''], false, 'text'),
            limit: new Field('limit', '100', [''], false, 'select', {hideLabel: true, labelType: "float"}, {
                menuPlacement: 'top',
                hasPortal: true
            })
        }
    }

    const getResourceName = () => {
        return Resources.ProfitMargin
    }

    const getQuery = () => {
        return {
            ...FieldsManager.getFieldKeyValues(queryFilterFields),
            order: sort,
            orderBy: sortBy
        }
    }

    const getTableOptions = (pagePath, translate) => {
        const tablePageDefaults = {
            version: 1,
            behaviour: {
                rowSelect: false,
                canAdjustWidth: true,
                hasMenu: true
            },
            style: {
                condensed: true,
                isGPUAccelerated: true
            }
        }

        return getDefaultTableOptions(getFields(), tablePageDefaults, pagePath, translate)
    }

    /** State
     ================================================================= */
    const [queryFilterFields, setQueryFilterFields] = useState(getQueryFields())
    const [tableOptions, setTableOptions] = useState(getTableOptions(pagePath, translate))
    const [isTableFullScreen, setIsTableFullScreen] = useState(false)
    const [sort, setSort] = useState("DESC")
    const [sortBy, setSortBy] = useState("GrossProfit")

    const metadata = {}

    /** Data events
     ================================================================= */
    const fetchData = () => {
        dispatch(getSecondResource({
            user: LocalStorage.get('user'),
            resource: getResourceName(),
            query: getQuery()
        }))
    }

    const downloadExcel = () => {
        dispatch(download({
            user: LocalStorage.get('user'),
            resource: getResourceName(),
            query: Object.assign({
                format: 'EXCEL',
                name: 'profit_margin_report_' + currentDate() + '.xlsx'
            }, getQuery())
        }))
    }

    const downloadInvoicesExpensesExcel = () => {
        dispatch(download({
            user: LocalStorage.get('user'),
            resource: Resources.WorkOrderExpenseVsInvoices,
            query: Object.assign({
                format: 'EXCEL',
                name: 'work_order_expenses_invoices' + currentDate() + '.xlsx'
            }, getQuery())
        }))
    }

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        fetchData()
    }, [sort, sortBy, queryFilterFields])

    /** UI events
     ================================================================= */
    const handleFilterInputChange = (name, value) => {
        let queryFieldsUpdate = FieldsManager.updateField(queryFilterFields, name, value)

        if (name === 'StartDate') {
            queryFieldsUpdate.EndDate.props.minDate = value
        }
        if (name === 'EndDate') {
            queryFieldsUpdate.StartDate.props.maxDate = value
        }

        setQueryFilterFields(queryFieldsUpdate)
    }

    const handleClearFiltersClick = (excludeAdditional = []) => {
        const queryFilterFieldsTmp = Object.assign({}, queryFilterFields)
        const defaultExcludedFields = ['limit']
        const excludedFields = defaultExcludedFields.concat(excludeAdditional)

        Object.values(queryFilterFieldsTmp).filter((it) => !excludedFields.includes(it.name)).forEach((it) => {
            FieldsManager.updateField(queryFilterFieldsTmp, it.name, '')
        })

        setQueryFilterFields(queryFilterFieldsTmp)
    }

    const handleFullScreenToggle = () => {
        setIsTableFullScreen(!isTableFullScreen)
    }

    const setOptions = (options) => {
        setTableOptions(options)
        saveTableColumns(pagePath, options)
    }

    const handleUpdateSort = (sortByTmp) => {
        setSort((sortByTmp === sortBy) ? (sort === "ASC" ? "DESC" : "ASC") : "DESC")
        setSortBy(sortByTmp)
    }

    /** Render
     ================================================================= */
    return (
        <React.Fragment>
            <div className={"flex items-center mb-4"}>
                <div className="flex mx-4 gap-4 flex-wrap">
                    <Badge addClass={"relative"} type="outline">
                        {isLoading && (
                            <div className="px-5">
                                <LoaderSmall/>
                            </div>
                        )}

                        {!isLoading && (
                            <Tippy content={
                                <div className="text-xs p-1 max-w-sm">
                                    <p className="mb-2">Only finalized invoice sales with positive quantities are included - core returns and vendor credits are excluded. The summary bar reflects totals across all products in the selected period, not just the current page.</p>

                                    <p className="font-semibold mb-1">Cost Analysis</p>
                                    <ul className="mb-2 space-y-0.5">
                                        <li><span className="font-medium">Purchased Qty</span> - Units purchased during the selected period</li>
                                        <li><span className="font-medium">Sold Qty</span> - Units sold during the selected period</li>
                                        <li><span className="font-medium">Period Avg Cost</span> - Average cost per unit of purchases in the period</li>
                                        <li><span className="font-medium">Weighted Avg Cost</span> - True average cost across entire purchase history plus opening inventory. Used for all profitability calculations.</li>
                                        <li><span className="font-medium">AvgSellPrice</span> - Average price charged to customers per unit</li>
                                        <li><span className="font-medium">Total Purchase Cost</span> - Total amount spent on purchases in the period</li>
                                    </ul>

                                    <p className="font-semibold mb-1">Revenue & Profitability</p>
                                    <ul className="space-y-0.5">
                                        <li><span className="font-medium">Total Revenue</span> - Total sales revenue from invoices</li>
                                        <li><span className="font-medium">COGS</span> - Cost of Goods Sold (SoldQty × WeightedAvgCost)</li>
                                        <li><span className="font-medium">GrossProfit</span> - Total profit dollars (Revenue - COGS)</li>

                                        <li><span className="font-medium">ProfitMargin %</span> - % of revenue kept as profit. 40% = $0.40 profit per $1.00 collected.</li>
                                        <li><span className="font-medium">Markup %</span> - % marked up above cost. Always higher than margin for the same sale.</li>
                                    </ul>
                                </div>
                            } allowHTML={true} maxWidth={400}>
                                <div className="flex items-center gap-1.5">
                                    <CurrencyDollarIcon className="w-5 h-5"/>
                                    <span>Products:</span>
                                    <span className="font-bold">{count}</span>
                                </div>
                            </Tippy>
                        )}
                    </Badge>

                    {!isLoading && !!count && (
                        <>
                            <Badge type="blue">
                                <span>Revenue: </span>
                                <span className="font-bold">${numberWithCommas(totalRevenue, 2)}</span>
                            </Badge>
                            <Badge type="orange">
                                <span>COGS: </span>
                                <span className="font-bold">${numberWithCommas(totalCOGS, 2)}</span>
                            </Badge>
                            <Badge type="green">
                                <span>Profit: </span>
                                <span className="font-bold">${numberWithCommas(totalGrossProfit, 2)}</span>
                            </Badge>
                            <Badge type={overallMargin >= 30 ? "green" : overallMargin >= 15 ? "yellow" : "red"}>
                                <span>Margin: </span>
                                <span className="font-bold">{numberWithCommas(overallMargin, 1)}%</span>
                            </Badge>
                        </>
                    )}
                </div>
            </div>

            <div className={"sm:flex justify-between"}>
                <ActiveFilters
                    addClass={"mb-2"}
                    filterFields={queryFilterFields}
                    onLabelClick={handleFilterInputChange}
                    persistFilters={[]}
                    onClearFiltersClick={handleClearFiltersClick}
                    translate={translate}
                />

                <div>
                    <Tippy content={translate('text.download_excel')}>
                        <button
                            className="btn btn-primary"
                            onClick={downloadInvoicesExpensesExcel}
                        >
                            Download expenses and invoices
                        </button>
                    </Tippy>

                    <Tippy content={translate('text.download_excel')}>
                        <button
                            className="btn-icon"
                            onClick={downloadExcel}
                        >
                            <DocumentArrowDownIcon className="w-6 h-6"/>
                        </button>
                    </Tippy>
                </div>
            </div>

            <TableCard
                className={
                    classNames(
                        "",
                        isTableFullScreen
                            ? "bg-inverse rounded-card fixed inset-1 -top-1 z-50"
                            : "flex flex-col h-dialog-body md:bg-inverse md:border border-tm-gray-300 md:shadow-card"
                    )}
            >
                <div>
                    <TableFilters
                        forceDialog
                        hideLimit
                        filterFields={excludeFields(queryFilterFields, ['StartDate', 'EndDate'])}
                        handleInputChange={handleFilterInputChange}
                        translate={translate}
                        onRefreshTable={fetchData}
                        selects={metadata}
                        onFullScreenToggle={handleFullScreenToggle}
                        isTableFullScreen={isTableFullScreen}
                        isLoading={isLoading}
                        customHtml={
                            <PayDateFilter
                                queryFields={queryFilterFields}
                                hasDateType={false}
                                translate={translate}
                                updateQueryFields={(queryFieldsUpdate) => {
                                    setQueryFilterFields(queryFieldsUpdate)
                                }}
                                onQueryChange={handleFilterInputChange}
                            />
                        }
                    />
                </div>

                <ResourceTable
                    tableKey={'ProductServiceID'}
                    data={data}
                    count={count}
                    fields={getFields()}
                    options={tableOptions}
                    isLoading={isLoading}
                    translate={translate}
                    sort={sort}
                    sortBy={sortBy}
                    onSortChange={handleUpdateSort}
                    saveTableOptions={setOptions}
                    maxHeightClass={"max-h-[calc(100vh-28rem)]"}
                    tfoot={!!count && {
                        SoldQty: numberWithCommas(totalSoldQty, 2),
                        TotalPurchaseCost: numberWithCommas(totalPurchaseCost, 2),
                        TotalRevenue: numberWithCommas(totalRevenue, 2),
                        COGS: numberWithCommas(totalCOGS, 2),
                        GrossProfit: numberWithCommas(totalGrossProfit, 2),
                        ProfitMarginPercent: numberWithCommas(overallMargin, 1)
                    }}
                />

                <NoRecordsTable
                    show={(data.length === 0) && !isLoading}
                    title={'No matching records found'}
                    className={"pb-12 pt-16 px-6"}
                />

                <TableCardFooter
                    show={!(!data.length && !isLoading)}
                >
                    <Pagination
                        count={count}
                        isLoading={isLoading}
                        handleQueryChange={handleFilterInputChange}
                        queryFields={queryFilterFields}
                        translate={translate}
                    />
                </TableCardFooter>
            </TableCard>
        </React.Fragment>
    )
}